/**
 * Video Analyzer Component
 * Full-featured video analysis with multi-video sync, drag-drop reordering,
 * Gemini MoE integration, and comprehensive media controls.
 *
 * Ported from theWIRE/SLAMPitchDashboard/app/analyzer/page.tsx
 * Converted from TypeScript to JavaScript, Next.js to React Router
 *
 * @module VideoAnalyzer
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MediaType } from '../../../lib/types';
import { analyzeMedia, drillDownAnalysis, GEMINI_MODELS } from '../../../lib/geminiService';
import { analyzeVideo as analyzeVideoApi, analyzePitcher, searchYouTubeVideos, analyzeMultipleYouTubeVideos } from '../../../utils/videoAnalyticsApi';
import { ANALYSIS_MODES, getModesBySport, getModeIdsBySport } from '../../../lib/analysisModes';
import { ModelSelector } from './ModelSelector';
import { AnalysisModeSelector } from './AnalysisModeSelector';
import { TrainingVideoLibrary } from './TrainingVideoLibrary';
import { parseFilename } from '../../../lib/filenameParser';
import { MediaCard } from './MediaCard';
import {
  UploadIcon,
  SendIcon,
  XIcon,
  PlayIcon,
  PauseIcon,
  StepBackIcon,
  StepForwardIcon
} from './Icons';
import { AnalysisReport } from './AnalysisReport';
import { StructuredAnalysisReport } from './StructuredAnalysisReport';
import { AnalysisChat } from './AnalysisChat';
import { SlamLoader } from '../../design-system/SlamLoader.jsx';
// REMOVED: BasketballShootingReportInline - no longer using forced MoE template
import { MatrixBackground } from './MatrixBackground';
import {
  MAX_FILE_SIZE_MB,
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_TYPES,
  ACCEPTED_CSV_TYPES,
} from '../../../lib/constants';
import { convertDocumentToText } from '../../../lib/documentConverter';
import { useTeam } from '../../../lib/teamConfig';

// ─── Response text extraction helper ──────────────────────────────────────────
function extractAnalysisText(analysis) {
  if (!analysis) return null;

  // If it's a string, try JSON.parse first — backend sometimes returns JSON-wrapped markdown
  if (typeof analysis === 'string') {
    const trimmed = analysis.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('"')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          const candidates = [
            parsed?.response,
            parsed?.analysis_result,
            parsed?.result,
            parsed?.data?.response,
            parsed?.data?.analysis_result,
            parsed?.text,
            parsed?.content,
            parsed?.message,
          ];
          const found = candidates.find(c => typeof c === 'string' && c.trim().length > 0);
          if (found) return found;
        }
      } catch (e) { /* not JSON, fall through */ }
    }
    return analysis;
  }

  // It's an object — extract the text field
  const candidates = [
    analysis?.response,
    analysis?.analysis_result,
    analysis?.result,
    analysis?.data?.response,
    analysis?.data?.analysis_result,
    analysis?.data?.result,
    analysis?.text,
    analysis?.content,
    analysis?.message,
  ];
  const found = candidates.find(c => typeof c === 'string' && c.trim().length > 0);
  if (found) return found;
  return '```json\n' + JSON.stringify(analysis, null, 2) + '\n```';
}

// Grid size icon component
const GridIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

// AnalysisPanel wrapper component - renders analysis results
// Uses unified StructuredAnalysisReport for beautiful card layout across ALL modes
const AnalysisPanel = ({ isLoading, result, mediaItems, analysisMode, onDrillDown, isDrillDownLoading, title, analysisMetadata }) => {
  if (!result) return null;

  const reportTitle = title || (analysisMode ? `${ANALYSIS_MODES[analysisMode]?.name || 'Video'} Analysis` : 'Video Analysis Report');

  return (
    <StructuredAnalysisReport
      markdown={result}
      analysisMode={analysisMode}
      title={reportTitle}
      mediaItems={mediaItems}
      analysisMetadata={analysisMetadata}
      onDrillDown={onDrillDown}
      isDrillDownLoading={isDrillDownLoading}
    />
  );
};

export function VideoAnalyzer({ className }) {
  // Get team config for sport-specific UI elements
  const { config } = useTeam();

  // Derive sport category from sport code for UI elements like MatrixBackground
  const getSportCategory = (sportCode) => {
    if (!sportCode) return 'basketball';
    const code = sportCode.toUpperCase();
    if (['MLB', 'MILB', 'CBB', 'NPB', 'KBO'].includes(code)) return 'baseball';
    if (['GOLF', 'PGA', 'LPGA'].includes(code)) return 'golf';
    if (['SOC', 'MSOC', 'WSOC', 'MLS', 'EPL', 'LALIGA', 'BUNDESLIGA', 'LIGUE1', 'SERIE_A', 'LIGA_MX', 'NWSL', 'USL', 'FC'].includes(code)) return 'soccer';
    // Football maps to basketball UI (no football-specific analysis modes yet)
    return 'basketball'; // Default for MBB, WBB, NBA, WNBA, PFB, CFB, NFL, etc.
  };
  const sportCategory = getSportCategory(config?.sportCode);

  const [items, setItems] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // Persist result across navigation using localStorage
  const resultStorageKey = `slam_video_result_${sportCategory || 'basketball'}`;
  const [result, setResult] = useState(() => {
    try {
      const saved = localStorage.getItem(resultStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const raw = parsed.result || null;
        // Clean up any JSON-wrapped strings from old saves
        return raw ? extractAnalysisText(raw) : null;
      }
    } catch {}
    return null;
  });
  const fileInputRef = useRef(null);
  const contextFileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);

  // YouTube ingestion state
  const [inputSource, setInputSource] = useState('file'); // 'file' or 'youtube'
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isYoutubeAnalyzing, setIsYoutubeAnalyzing] = useState(false);

  // Multi-video selection state
  const [selectedVideoIds, setSelectedVideoIds] = useState(new Set());
  const [selectedYoutubeVideos, setSelectedYoutubeVideos] = useState([]);

  // Pitcher analysis state (Five-Dimension mode)
  const [pitcherMode, setPitcherMode] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [playerAge, setPlayerAge] = useState(25);
  const [selectedDimensions, setSelectedDimensions] = useState([
    'pitch_tipping', 'mechanical_efficiency', 'injury_risk', 'pitch_optimization', 'development'
  ]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [athleteSearch, setAthleteSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [pitcherResult, setPitcherResult] = useState(null);
  const canvasRef = useRef(null);
  const resultsPanelRef = useRef(null);

  // Notification state for analysis completion
  const [showNotification, setShowNotification] = useState(false);

  // Drill-down state for expert deep-dive analysis
  const [isDrillDownLoading, setIsDrillDownLoading] = useState(false);
  const [drillDownResult, setDrillDownResult] = useState(null);

  // Context files state (PDF, TXT, DOCX, XLSX)
  const [contextFiles, setContextFiles] = useState([]);
  const [contextPanelOpen, setContextPanelOpen] = useState(true);

  // Analysis metadata for report "Data Analyzed" section
  const [analysisMetadata, setAnalysisMetadata] = useState(() => {
    try {
      const saved = localStorage.getItem(resultStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.analysisMetadata || null;
      }
    } catch {}
    return null;
  });

  // Persist result + metadata to localStorage whenever result changes
  useEffect(() => {
    try {
      if (result) {
        localStorage.setItem(resultStorageKey, JSON.stringify({ result, analysisMetadata }));
      } else {
        localStorage.removeItem(resultStorageKey);
      }
    } catch {}
  }, [result, analysisMetadata, resultStorageKey]);

  // Video Control State
  const videoRefs = useRef(new Map());
  const [isPlaying, setIsPlaying] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  // Per-video start points (in seconds) - allows manual alignment of multi-angle footage
  const [videoStartPoints, setVideoStartPoints] = useState(new Map());

  // UX feedback for sync operations
  const [syncStatus, setSyncStatus] = useState(null);

  // Thumbnail size control (percentage: 50-150, default 100)
  const [thumbnailScale, setThumbnailScale] = useState(100);
  // Grid overlay toggle for alignment comparison
  const [showGrid, setShowGrid] = useState(true);

  // Model selection state - persisted to localStorage to prevent flickering on re-renders
  const [selectedModel, setSelectedModel] = useState(() => {
    // Initialize from localStorage if available, default to 'pro'
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('slam_video_analyzer_model');
      if (saved && ['flash', 'pro', 'max'].includes(saved)) {
        return saved;
      }
    }
    return 'pro';
  });

  // Persist model selection to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedModel) {
      localStorage.setItem('slam_video_analyzer_model', selectedModel);
    }
  }, [selectedModel]);

  // Stable model change handler to prevent re-renders
  const handleModelChange = useCallback((model) => {
    setSelectedModel(model);
  }, []);

  // Get default analysis mode based on current sport
  const defaultAnalysisMode = useMemo(() => {
    const sportModes = getModeIdsBySport(sportCategory);
    if (sportModes.length > 0) return sportModes[0];
    if (sportCategory === 'baseball') return 'pitch-tipping';
    if (sportCategory === 'golf') return 'golf-swing';
    if (sportCategory === 'soccer') return 'soccer-tactical';
    return 'basketball-analysis';
  }, [sportCategory]);

  // Analysis mode state - defaults based on sport category
  const [analysisMode, setAnalysisMode] = useState(defaultAnalysisMode);

  // Update analysis mode when sport changes (e.g., switching teams)
  useEffect(() => {
    const validModes = getModeIdsBySport(sportCategory);
    if (!validModes.includes(analysisMode)) {
      const fallback = sportCategory === 'baseball' ? 'pitch-tipping'
        : sportCategory === 'golf' ? 'golf-swing'
        : sportCategory === 'soccer' ? 'soccer-tactical'
        : 'basketball-analysis';
      setAnalysisMode(validModes[0] || fallback);
    }
  }, [sportCategory, analysisMode]);

  // Reset pitcher mode when switching away from baseball
  useEffect(() => {
    if (sportCategory !== 'baseball') {
      setPitcherMode(false);
      setPitcherResult(null);
      setPlayerName('');
      setPlayerAge(25);
      setCustomPrompt('');
      setSelectedDimensions(['pitch_tipping', 'mechanical_efficiency', 'injury_risk', 'pitch_optimization', 'development']);
      setSearchResults([]);
      setSelectedYoutubeVideos([]);
      setSelectedVideoIds(new Set());
    }
  }, [sportCategory]);

  // Build metadata snapshot of what data was analyzed
  const buildAnalysisMetadata = useCallback(() => ({
    timestamp: new Date().toISOString(),
    mode: analysisMode,
    modeName: ANALYSIS_MODES[analysisMode]?.name || analysisMode,
    model: selectedModel,
    modelName: GEMINI_MODELS[selectedModel]?.name || selectedModel,
    sport: sportCategory,
    userPrompt: prompt.trim() || ANALYSIS_MODES[analysisMode]?.defaultPrompt || null,
    files: items.map(item => ({
      name: item.name || item.file?.name || 'Unknown',
      size: item.file?.size || 0,
      mediaType: item.type || 'unknown',
      source: item.youtubeUrl ? 'YouTube' : 'Upload',
    })),
    contextFiles: contextFiles.filter(cf => cf.status === 'ready').map(cf => ({
      name: cf.name,
      type: cf.type,
    })),
    youtubeVideos: selectedYoutubeVideos.map(v => ({
      title: v.title,
      url: v.url,
      channel: v.channelTitle,
    })),
  }), [items, contextFiles, selectedYoutubeVideos, analysisMode, selectedModel, sportCategory, config?.sportCode, prompt]);

  // View mode state (upload vs training-library)
  const [viewMode, setViewMode] = useState('upload');

  // Drag-and-drop reordering state
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);

  // Calculate optimal grid columns based on item count and thumbnail scale
  const getGridStyle = useCallback(() => {
    const scale = thumbnailScale / 100;
    const baseSize = 200;
    const scaledSize = Math.round(baseSize * scale);

    return {
      gridTemplateColumns: `repeat(auto-fill, minmax(${Math.max(scaledSize, 100)}px, ${scaledSize}px))`,
      gap: `${Math.max(4, Math.round(12 * scale))}px`,
      justifyContent: 'center',
    };
  }, [thumbnailScale]);

  const processFile = useCallback((file) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File ${file.name} is too large. Max size is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    let type = MediaType.UNKNOWN;
    if (ACCEPTED_IMAGE_TYPES.includes(file.type)) type = MediaType.IMAGE;
    else if (ACCEPTED_VIDEO_TYPES.includes(file.type)) type = MediaType.VIDEO;
    else if (ACCEPTED_CSV_TYPES.includes(file.type) || file.name.endsWith('.csv')) type = MediaType.CSV;

    if (type === MediaType.UNKNOWN) {
      setError(`File type for ${file.name} is not supported.`);
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const reader = new FileReader();
    const metadata = parseFilename(file.name);

    reader.onload = (e) => {
      const base64Data = e.target?.result;
      setItems((prev) => [
        ...prev,
        {
          id: Math.random().toString(36).substring(7),
          file,
          type,
          previewUrl,
          base64Data,
          mimeType: file.type,
          name: file.name,
          metadata: metadata || undefined
        }
      ]);
      setError(null);
    };
    reader.readAsDataURL(file);
  }, []);

  // Process context document files (PDF, TXT, DOCX, XLSX, XML)
  const processContextFile = useCallback(async (file) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['pdf', 'txt', 'docx', 'xlsx', 'doc', 'xls', 'xml'];

    if (!validExtensions.includes(extension)) {
      setError(`File type .${extension} is not supported for context files.`);
      return;
    }

    const id = Math.random().toString(36).substring(7);
    const contextFile = {
      id,
      file,
      name: file.name,
      type: extension,
      status: 'processing',
    };

    setContextFiles(prev => [...prev, contextFile]);

    try {
      const result = await convertDocumentToText(file);

      if (result.success) {
        setContextFiles(prev =>
          prev.map(cf =>
            cf.id === id
              ? { ...cf, status: 'ready', textContent: result.text }
              : cf
          )
        );
      } else {
        setContextFiles(prev =>
          prev.map(cf =>
            cf.id === id
              ? { ...cf, status: 'error', error: result.error }
              : cf
          )
        );
      }
    } catch (err) {
      setContextFiles(prev =>
        prev.map(cf =>
          cf.id === id
            ? { ...cf, status: 'error', error: 'Failed to process file' }
            : cf
        )
      );
    }
  }, []);

  const handleRemoveContextFile = (id) => {
    setContextFiles(prev => prev.filter(cf => cf.id !== id));
  };

  const handleContextFileChange = (e) => {
    if (e.target.files) {
      Array.from(e.target.files).forEach(processContextFile);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      Array.from(e.dataTransfer.files).forEach(processFile);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      Array.from(e.target.files).forEach(processFile);
    }
  };

  const handleRemove = (id) => {
    setItems(items.filter(i => i.id !== id));
    videoRefs.current.delete(id);
  };

  const handleVideoRef = (id, el) => {
    if (el) {
      videoRefs.current.set(id, el);
    } else {
      videoRefs.current.delete(id);
    }
  };

  // Handle per-video start point changes from thumbnail scrubbing
  const handleStartPointChange = useCallback((id, startPointSeconds) => {
    setVideoStartPoints(prev => new Map(prev).set(id, startPointSeconds));

    // Show UX feedback
    const videoName = items.find(item => item.id === id)?.name || 'Video';
    setSyncStatus({
      videoId: id,
      message: `${videoName.split('.')[0]}: Start point set at ${startPointSeconds.toFixed(2)}s`
    });

    // Clear status after 2 seconds
    setTimeout(() => setSyncStatus(null), 2000);
  }, [items]);

  // Clear all start points
  const clearAllStartPoints = useCallback(() => {
    setVideoStartPoints(new Map());
    setSyncStatus({ videoId: 'all', message: 'All sync points cleared' });
    setTimeout(() => setSyncStatus(null), 2000);

    // Reset all videos to 0
    videoRefs.current.forEach((video) => {
      video.currentTime = 0;
    });
    setSliderValue(0);
  }, []);

  // Calculate the maximum playable duration (shortest video from its start point)
  const getMaxPlayableDuration = useCallback(() => {
    let maxDuration = Infinity;
    videoRefs.current.forEach((video, id) => {
      if (!Number.isNaN(video.duration)) {
        const startPoint = videoStartPoints.get(id) || 0;
        const remainingDuration = video.duration - startPoint;
        if (remainingDuration < maxDuration) {
          maxDuration = remainingDuration;
        }
      }
    });
    return maxDuration === Infinity ? 10 : maxDuration; // Default to 10s if no videos
  }, [videoStartPoints]);

  // Sync all videos based on timeline percentage
  const syncVideos = useCallback((percent) => {
    const maxDuration = getMaxPlayableDuration();
    const timelinePosition = (percent / 100) * maxDuration;

    videoRefs.current.forEach((video, id) => {
      if (!Number.isNaN(video.duration)) {
        const startPoint = videoStartPoints.get(id) || 0;
        const targetTime = startPoint + timelinePosition;
        video.currentTime = Math.max(0, Math.min(video.duration, targetTime));
      }
    });
  }, [videoStartPoints, getMaxPlayableDuration]);

  const handleSliderChange = (e) => {
    const val = parseFloat(e.target.value);
    setSliderValue(val);
    setIsPlaying(false);
    videoRefs.current.forEach(v => v.pause());
    syncVideos(val);
  };

  const togglePlay = useCallback(() => {
    const shouldPlay = !isPlaying;
    setIsPlaying(shouldPlay);

    if (shouldPlay) {
      // If at the end, reset to start points
      if (sliderValue >= 99) {
        setSliderValue(0);
        syncVideos(0);
      }

      videoRefs.current.forEach(video => video.play());
    } else {
      videoRefs.current.forEach(video => video.pause());
    }
  }, [isPlaying, sliderValue, syncVideos]);

  const stepFrame = useCallback((direction) => {
    setIsPlaying(false);
    videoRefs.current.forEach(video => video.pause());

    const step = 0.033 * (direction === 'forward' ? 1 : -1);
    const maxDuration = getMaxPlayableDuration();

    // Step all videos together
    videoRefs.current.forEach((video, id) => {
      if (!Number.isNaN(video.duration)) {
        const startPoint = videoStartPoints.get(id) || 0;
        let newTime = video.currentTime + step;

        // Clamp to valid range (start point to end of video)
        newTime = Math.max(startPoint, Math.min(video.duration, newTime));
        video.currentTime = newTime;
      }
    });

    // Update slider based on first video
    const firstVideo = videoRefs.current.values().next().value;
    const firstVideoId = videoRefs.current.keys().next().value;
    if (firstVideo && firstVideoId && !Number.isNaN(firstVideo.duration)) {
      const startPoint = videoStartPoints.get(firstVideoId) || 0;
      const elapsed = firstVideo.currentTime - startPoint;
      const newSliderVal = maxDuration > 0 ? (elapsed / maxDuration) * 100 : 0;
      setSliderValue(Math.max(0, Math.min(100, newSliderVal)));
    }
  }, [videoStartPoints, getMaxPlayableDuration]);

  // Drag-and-drop handlers for reordering
  const handleDragStart = (e, itemId) => {
    setDraggedItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.dropEffect = 'move';

    // Prevent text selection during drag
    e.dataTransfer.setData('text/html', '');

    // Set transparent drag image for cleaner visual feedback
    const dragImg = document.createElement('div');
    dragImg.style.opacity = '0';
    document.body.appendChild(dragImg);
    e.dataTransfer.setDragImage(dragImg, 0, 0);
    setTimeout(() => document.body.removeChild(dragImg), 0);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDragOverItemId(null);
  };

  const handleDragOverItem = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedItemId && draggedItemId !== itemId) {
      setDragOverItemId(itemId);
    }
  };

  const handleDropOnItem = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedItemId || draggedItemId === targetId) {
      setDraggedItemId(null);
      setDragOverItemId(null);
      return;
    }

    // Reorder the items array
    setItems(prevItems => {
      const newItems = [...prevItems];
      const draggedIndex = newItems.findIndex(item => item.id === draggedItemId);
      const targetIndex = newItems.findIndex(item => item.id === targetId);

      if (draggedIndex === -1 || targetIndex === -1) return prevItems;

      // Remove dragged item and insert at target position
      const [draggedItem] = newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, draggedItem);

      return newItems;
    });

    setDraggedItemId(null);
    setDragOverItemId(null);
  };

  // Sync Slider while playing - calculates position relative to start points
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = window.setInterval(() => {
        const firstVideo = videoRefs.current.values().next().value;
        const firstVideoId = videoRefs.current.keys().next().value;
        if (firstVideo && firstVideoId && !Number.isNaN(firstVideo.duration)) {
          const startPoint = videoStartPoints.get(firstVideoId) || 0;
          const maxDuration = getMaxPlayableDuration();
          const elapsed = firstVideo.currentTime - startPoint;
          const pct = maxDuration > 0 ? (elapsed / maxDuration) * 100 : 0;

          setSliderValue(Math.max(0, Math.min(100, pct)));

          // Stop at end of synced playback
          if (pct >= 100) {
            setIsPlaying(false);
            videoRefs.current.forEach(video => video.pause());
          }
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isPlaying, videoStartPoints, getMaxPlayableDuration]);

  const handleAnalyze = async () => {
    if (items.length === 0 && !prompt.trim()) return;

    setIsAnalyzing(true);
    setResult(null);
    setError(null);
    setShowNotification(false);

    try {
      // Use mode-specific default prompt if no prompt provided
      const modeConfig = ANALYSIS_MODES[analysisMode];
      const effectivePrompt = prompt.trim() || modeConfig.defaultPrompt;

      const analysis = await analyzeMedia({
        mediaItems: items,
        analysisMode,
        contextFiles,
        model: selectedModel,
        // USER PROMPT FIRST - Pass the user's prompt with highest priority
        // If user provided custom prompt, it takes precedence over template defaults
        userPrompt: prompt.trim() ? prompt.trim() : effectivePrompt,
      });
      setResult(analysis);
      setAnalysisMetadata(buildAnalysisMetadata());

      // Save analysis to database for history/retrieval
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId') || 'anonymous';
        const videoUrl = items[0]?.previewUrl || items[0]?.base64Data?.substring(0, 100) || null;

        await fetch('/api/analysis/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            videoUrl,
            analysisData: analysis,
            analysisMode,
            model: selectedModel,
            title: `${ANALYSIS_MODES[analysisMode]?.name || 'Video'} Analysis`,
            teamId: config?.teamId || null,
          }),
        });
        console.log('[VideoAnalyzer] Analysis saved to database');
      } catch (saveErr) {
        // Don't fail the analysis if save fails - just log it
        console.warn('[VideoAnalyzer] Failed to save analysis:', saveErr.message);
      }

      // Show notification popup
      setShowNotification(true);
      // Auto-hide notification after 10 seconds
      setTimeout(() => setShowNotification(false), 10000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to analyze media.";
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle drill-down into specific expert analysis
  const handleDrillDown = async (expertName) => {
    if (items.length === 0) return;

    setIsDrillDownLoading(true);
    setError(null);

    try {
      const drillResult = await drillDownAnalysis({
        mediaItems: items,
        expertName,
        contextFiles,
      });

      // Append drill-down result to the main result
      const separator = '\n\n---\n\n## 🔍 Drill-Down Analysis: ' + expertName.replace(/_/g, ' ') + '\n\n';
      setResult(prev => prev + separator + drillResult);

      // Show notification
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 10000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to perform drill-down analysis.';
      setError(errorMessage);
    } finally {
      setIsDrillDownLoading(false);
    }
  };

  // Quick/Simple Analysis - fast single-prompt analysis with flash model
  const handleSimpleAnalysis = async () => {
    if (items.length === 0) return;

    setIsAnalyzing(true);
    setResult(null);
    setError(null);
    setShowNotification(false);

    try {
      const modeConfig = ANALYSIS_MODES['simple-mechanics'];
      const analysis = await analyzeMedia({
        mediaItems: items,
        analysisMode: 'simple-mechanics',
        contextFiles,
        model: 'flash', // Force flash for speed
        userPrompt: modeConfig.defaultPrompt,
      });
      setResult(analysis);
      setAnalysisMetadata(buildAnalysisMetadata());

      // Save analysis to database
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId') || 'anonymous';
        const videoUrl = items[0]?.previewUrl || items[0]?.base64Data?.substring(0, 100) || null;

        await fetch('/api/analysis/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            videoUrl,
            analysisData: analysis,
            analysisMode: 'simple-mechanics',
            model: 'flash',
            title: 'Quick Mechanics Analysis',
            teamId: config?.teamId || null,
          }),
        });
      } catch (saveErr) {
        console.warn('[VideoAnalyzer] Failed to save analysis:', saveErr.message);
      }

      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 10000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze media.';
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // YouTube URL analysis handler (supports multi-video)
  const handleYoutubeAnalyze = async () => {
    // Determine which URLs to analyze
    const urls = selectedYoutubeVideos.length > 0
      ? selectedYoutubeVideos.map(v => v.url)
      : youtubeUrl.trim() ? [youtubeUrl.trim()] : [];

    if (urls.length === 0) return;

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    for (const url of urls) {
      if (!youtubeRegex.test(url)) {
        setError(`Invalid YouTube URL: ${url}`);
        return;
      }
    }

    setIsYoutubeAnalyzing(true);
    setIsAnalyzing(true);
    setResult(null);
    setError(null);

    try {
      const modeConfig = ANALYSIS_MODES[analysisMode];
      const effectivePrompt = prompt.trim() || modeConfig?.defaultPrompt || 'Analyze this video';

      if (urls.length === 1) {
        // Single video — use analyzeMedia (same path as file uploads)
        // Gemini natively supports YouTube URLs as fileData
        const ytUrl = urls[0].startsWith('http') ? urls[0] : `https://${urls[0]}`;
        const youtubeMediaItems = [{
          id: `yt-${Date.now()}`,
          youtubeUrl: ytUrl,
          type: 'video',
          name: selectedYoutubeVideos[0]?.title || ytUrl,
        }];

        const analysis = await analyzeMedia({
          mediaItems: youtubeMediaItems,
          analysisMode,
          contextFiles,
          model: selectedModel,
          userPrompt: prompt.trim() ? prompt.trim() : effectivePrompt,
        });
        setResult(analysis);
      } else {
        // Multi-video — use slam-agents endpoint (Gemini only allows 1 YouTube URL per call)
        const analysis = await analyzeMultipleYouTubeVideos({
          youtubeUrls: urls.map(u => u.startsWith('http') ? u : `https://${u}`),
          playerName: playerName || 'Athlete',
          customPrompt: effectivePrompt,
          sport: config?.sportCode || (sportCategory === 'baseball' ? 'MLB' : sportCategory === 'golf' ? 'PGA' : sportCategory === 'soccer' ? 'MLS' : 'NBA'),
        });
        setResult(extractAnalysisText(analysis));
      }
      setAnalysisMetadata(buildAnalysisMetadata());

      // Save analysis to database (fire-and-forget, same as handleAnalyze)
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId') || 'anonymous';
        await fetch('/api/analysis/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            videoUrl: urls[0],
            analysisData: result,
            analysisMode,
            model: selectedModel,
            title: `${ANALYSIS_MODES[analysisMode]?.name || 'Video'} Analysis`,
            teamId: config?.teamId || null,
          }),
        });
      } catch (saveErr) {
        console.warn('[VideoAnalyzer] Failed to save YouTube analysis:', saveErr.message);
      }

      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 10000);
    } catch (err) {
      const msg = err?.message || 'YouTube analysis failed';
      if (msg.includes('timeout') || msg.includes('timed out')) {
        setError('YouTube video processing timed out. Try shorter videos (under 5 minutes each).');
      } else if (msg.includes('private') || msg.includes('unavailable')) {
        setError('A YouTube video is private or unavailable. Try different videos.');
      } else if (msg.includes('Could not extract') || msg.includes('download')) {
        setError('Could not download YouTube video. Try different videos or upload files directly.');
      } else {
        setError(msg);
      }
    } finally {
      setIsYoutubeAnalyzing(false);
      setIsAnalyzing(false);
    }
  };

  // Five-dimension pitcher analysis handler (supports multi-video)
  const handlePitcherAnalyze = async () => {
    // Determine which URLs to analyze
    const urls = selectedYoutubeVideos.length > 0
      ? selectedYoutubeVideos.map(v => v.url)
      : youtubeUrl.trim() ? [youtubeUrl.trim()] : [];

    if (urls.length === 0 || !playerName.trim()) return;

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    for (const url of urls) {
      if (!youtubeRegex.test(url)) {
        setError(`Invalid YouTube URL: ${url}`);
        return;
      }
    }

    setIsYoutubeAnalyzing(true);
    setIsAnalyzing(true);
    setResult(null);
    setPitcherResult(null);
    setError(null);

    try {
      if (urls.length === 1) {
        // Single video — use existing endpoint
        const response = await analyzePitcher({
          youtubeUrl: urls[0],
          playerName,
          age: playerAge,
          dimensions: selectedDimensions.length < 5 ? selectedDimensions : undefined,
          customPrompt: customPrompt || undefined,
          sport: config?.sportCode || (sportCategory === 'baseball' ? 'MLB' : sportCategory === 'golf' ? 'PGA' : sportCategory === 'soccer' ? 'MLS' : 'NBA'),
        });
        setPitcherResult(response);
      } else {
        // Multi-video — use new endpoint
        // analyzeMultipleYouTubeVideos already imported at top
        const response = await analyzeMultipleYouTubeVideos({
          youtubeUrls: urls,
          playerName,
          age: playerAge,
          dimensions: selectedDimensions.length < 5 ? selectedDimensions : undefined,
          customPrompt: customPrompt || undefined,
          sport: config?.sportCode || (sportCategory === 'baseball' ? 'MLB' : sportCategory === 'golf' ? 'PGA' : sportCategory === 'soccer' ? 'MLS' : 'NBA'),
        });
        setPitcherResult(response);
      }
      setAnalysisMetadata(buildAnalysisMetadata());
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 10000);
    } catch (err) {
      setError(err?.message || 'Pitcher analysis failed. Please try again.');
    } finally {
      setIsYoutubeAnalyzing(false);
      setIsAnalyzing(false);
    }
  };

  // YouTube athlete search handler
  const handleAthleteSearch = async () => {
    if (!athleteSearch.trim()) return;
    setIsSearching(true);
    setSearchError(null);
    setSearchResults([]);
    try {
      const results = await searchYouTubeVideos({
        query: athleteSearch.trim(),
        maxResults: 8,
        sport: sportCategory,
      });
      // Deduplicate by videoId (YouTube API can return duplicates)
      const seen = new Set();
      const unique = results.filter(r => {
        if (seen.has(r.videoId)) return false;
        seen.add(r.videoId);
        return true;
      });
      setSearchResults(unique);
      if (unique.length === 0) {
        setSearchError('No videos found. Try a different search term.');
      }
    } catch (err) {
      setSearchError(err.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  // Toggle a video selection in multi-select mode
  const toggleVideoSelection = (result) => {
    setSelectedVideoIds(prev => {
      const next = new Set(prev);
      if (next.has(result.videoId)) {
        next.delete(result.videoId);
        setSelectedYoutubeVideos(vids => vids.filter(v => v.videoId !== result.videoId));
      } else {
        if (next.size >= 10) {
          setSearchError('Maximum 10 videos can be selected at once.');
          return prev;
        }
        next.add(result.videoId);
        setSelectedYoutubeVideos(vids =>
          vids.some(v => v.videoId === result.videoId) ? vids : [...vids, result]
        );
      }
      return next;
    });
    // Auto-fill player name from search query (regardless of mode)
    if (!playerName && athleteSearch.trim()) {
      setPlayerName(athleteSearch.trim());
    }
  };

  // Remove a video from selection
  const removeSelectedVideo = (videoId) => {
    setSelectedVideoIds(prev => {
      const next = new Set(prev);
      next.delete(videoId);
      return next;
    });
    setSelectedYoutubeVideos(vids => vids.filter(v => v.videoId !== videoId));
  };

  // Clear all selected videos
  const clearSelectedVideos = () => {
    setSelectedVideoIds(new Set());
    setSelectedYoutubeVideos([]);
  };

  // Select a single search result — auto-fill URL (legacy single-select via paste)
  const selectSearchResult = (result) => {
    const searchName = athleteSearch.trim();
    setYoutubeUrl(result.url);
    setSearchResults([]);
    setAthleteSearch('');
    if (pitcherMode && !playerName && searchName) {
      setPlayerName(searchName);
    }
  };

  const toggleDimension = (dim) => {
    setSelectedDimensions(prev =>
      prev.includes(dim)
        ? prev.filter(d => d !== dim)
        : [...prev, dim]
    );
  };

  // Scroll to results panel
  const scrollToResults = () => {
    setShowNotification(false);
    resultsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const hasVideos = items.some(i => i.type === MediaType.VIDEO);
  const videoCount = items.filter(i => i.type === MediaType.VIDEO).length;

  // Inline styles for CSS animations (replacing styled-jsx)
  const digitalBarAnimation = `
    @keyframes digital-bar {
      0%, 100% {
        height: 12px;
        background-color: rgba(255, 215, 0, 0.2);
      }
      50% {
        height: 24px;
        background-color: rgba(255, 215, 0, 0.8);
      }
    }

    @keyframes xeroxScan {
      0% {
        top: 0%;
      }
      45% {
        top: 100%;
      }
      50% {
        top: 100%;
      }
      95% {
        top: 0%;
      }
      100% {
        top: 0%;
      }
    }
  `;

  return (
    <div className={`page-container bg-background text-foreground flex flex-col relative font-sans min-h-screen ${className || ''}`}>
      {/* Inject CSS Animation */}
      <style>{digitalBarAnimation}</style>

      {/* Matrix Background Overlay */}
      <MatrixBackground isActive={isAnalyzing} phase="analyzing" sportCategory={sportCategory} />

      {/* Simple Header (replacing AppHeader) */}
      <header className="flex-shrink-0 border-b border-border/50 bg-background/80 backdrop-blur-sm z-30">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-foreground">Video Analyzer</h1>
            <span className="text-sm font-medium text-primary">
              AI Analysis
            </span>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-muted/30 rounded-full p-1 border border-border/50">
            <button
              onClick={() => setViewMode('upload')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                viewMode === 'upload'
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center gap-2">
                <UploadIcon className="w-4 h-4" />
                Upload
              </span>
            </button>
            {sportCategory === 'baseball' && (
            <button
              onClick={() => setViewMode('training-library')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                viewMode === 'training-library'
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                Library
              </span>
            </button>
            )}
            {sportCategory === 'baseball' && (
            <button
              onClick={() => setViewMode('moe-analyzer')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                viewMode === 'moe-analyzer'
                  ? 'bg-gradient-to-r from-yellow-500 to-yellow-400 text-black shadow-lg'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
                MoE Intel
              </span>
            </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Workspace */}
      <main className="flex-1 flex flex-col items-center p-4 sm:p-6 w-full max-w-[1800px] mx-auto z-20 relative overflow-y-auto">
        {/* Error Notification */}
        {error && (
          <div className="w-full max-w-2xl mb-4 bg-destructive/20 border border-destructive/50 text-destructive px-4 py-3 rounded-lg text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)}><XIcon className="w-4 h-4" /></button>
          </div>
        )}

        {/* Context Files Panel */}
        {(contextFiles.length > 0 || items.length > 0) && (
          <div className="w-full mb-4">
            <button
              onClick={() => setContextPanelOpen(!contextPanelOpen)}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <svg
                className={`w-4 h-4 transition-transform ${contextPanelOpen ? 'rotate-90' : ''}`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Context Files ({contextFiles.length})
            </button>

            {contextPanelOpen && (
              <div className="flex flex-wrap gap-2 p-3 bg-card/50 rounded-xl border border-border/50">
                {contextFiles.map(cf => (
                  <div
                    key={cf.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      cf.status === 'ready'
                        ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                        : cf.status === 'error'
                        ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                        : 'bg-muted/50 border border-border text-muted-foreground'
                    }`}
                  >
                    {cf.status === 'processing' && (
                      <SlamLoader size={12} />
                    )}
                    {cf.status === 'ready' && (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                    {cf.status === 'error' && (
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M15 9l-6 6M9 9l6 6" />
                      </svg>
                    )}
                    <span className="max-w-[150px] truncate">{cf.name}</span>
                    <span className="text-xs opacity-60 uppercase">{cf.type}</span>
                    <button
                      onClick={() => handleRemoveContextFile(cf.id)}
                      className="ml-1 hover:text-foreground transition-colors"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                <input
                  ref={contextFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleContextFileChange}
                  accept=".pdf,.txt,.docx,.xlsx,.doc,.xls,.xml"
                />

                <button
                  onClick={() => contextFileInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-muted/30 border border-dashed border-border hover:border-primary/50 hover:text-primary transition-colors text-muted-foreground"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add Context
                </button>
              </div>
            )}
          </div>
        )}

        {/* Training Video Library View */}
        {viewMode === 'training-library' ? (
          <div className="w-full">
            <TrainingVideoLibrary
              mode="individual"
              onSelectForMoE={(videoKey, analysis) => {
                console.log('Selected for analysis:', videoKey, analysis);
              }}
            />
          </div>
        ) : viewMode === 'moe-analyzer' ? (
          <div className="w-full">
            <TrainingVideoLibrary
              mode="moe"
              onSelectForMoE={(videoKey, analysis) => {
                console.log('MoE analysis complete:', videoKey, analysis);
              }}
            />
          </div>
        ) : (
        /* Video Canvas Area (Drop Zone) */
        <div
          ref={canvasRef}
          className={`
            w-full border-2 border-dashed rounded-3xl transition-all duration-300 relative flex flex-col
            ${isAnalyzing
              ? 'bg-background/60 border-primary/50 shadow-[0_0_60px_rgba(255,215,0,0.3)]'
              : dragActive
                ? 'border-primary bg-gradient-to-br from-primary/15 via-background to-primary/10 shadow-[0_0_40px_rgba(255,215,0,0.25)]'
                : 'border-primary/30 bg-gradient-to-br from-card via-background to-card hover:border-primary/50 hover:shadow-[0_0_20px_rgba(255,215,0,0.1)]'}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={(e) => {
            if (e.target === e.currentTarget && items.length === 0) fileInputRef.current?.click();
          }}
        >
          {/* Analysis Overlay — clean scan line + status pill */}
          {isAnalyzing && (
            <>
              {/* Subtle corner brackets */}
              <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 border-primary/40 z-30 pointer-events-none rounded-tl-lg" />
              <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 border-primary/40 z-30 pointer-events-none rounded-tr-lg" />
              <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 border-primary/40 z-30 pointer-events-none rounded-bl-lg" />
              <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 border-primary/40 z-30 pointer-events-none rounded-br-lg" />

              {/* Single scan line */}
              <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden rounded-3xl">
                <div
                  className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/80 to-transparent"
                  style={{
                    animation: 'xeroxScan 3s ease-in-out infinite',
                    boxShadow: '0 0 12px 4px rgba(255, 215, 0, 0.3)',
                  }}
                />
              </div>

              {/* Status pill */}
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                <div className="flex items-center gap-2.5 px-5 py-2.5 bg-background/80 backdrop-blur-sm border border-primary/30 rounded-full">
                  <SlamLoader size={12} variant="pulse" />
                  <span className="text-sm font-mono font-semibold text-primary tracking-wider">
                    ANALYZING
                  </span>
                  <span className="text-primary/60 font-mono animate-pulse">...</span>
                </div>
              </div>
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleChange}
            accept={[...ACCEPTED_IMAGE_TYPES, ...ACCEPTED_VIDEO_TYPES, ...ACCEPTED_CSV_TYPES].join(',')}
          />

          {items.length === 0 && !isYoutubeAnalyzing ? (
            <div
              className="flex-1 flex items-center justify-center px-4 py-4"
              onClick={() => inputSource === 'file' ? fileInputRef.current?.click() : null}
            >
              {/* Vertical Layout: Mode Selector on top, Upload Zone below */}
              <div className="flex flex-col items-center gap-4 w-full max-w-4xl">
                {/* Top: Mode Selector (horizontal template buttons) */}
                <div className="w-full" onClick={(e) => e.stopPropagation()}>
                  <AnalysisModeSelector
                    currentMode={analysisMode}
                    onModeChange={setAnalysisMode}
                    disabled={false}
                    sportCategory={sportCategory}
                    compact={true}
                  />
                </div>

                {/* Source Toggle: File Upload / YouTube */}
                <div className="flex items-center gap-1 bg-muted/30 rounded-full p-1 border border-border/50" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setInputSource('file')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      inputSource === 'file'
                        ? 'bg-primary text-primary-foreground shadow-lg'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <UploadIcon className="w-4 h-4" />
                      File Upload
                    </span>
                  </button>
                  <button
                    onClick={() => setInputSource('youtube')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      inputSource === 'youtube'
                        ? 'bg-red-600 text-white shadow-lg'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                      </svg>
                      YouTube
                    </span>
                  </button>
                </div>

                {/* File Upload Zone */}
                {inputSource === 'file' && (
                  <div className="w-full max-w-md flex flex-col items-center justify-center text-center cursor-pointer border-2 border-dashed border-primary/30 rounded-xl hover:border-primary/60 transition-colors bg-primary/5 p-6">
                    <div className="relative w-16 h-16 mb-3">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/25 to-transparent animate-pulse" />
                      <div className="absolute inset-0 rounded-full border border-primary/40 flex items-center justify-center bg-gradient-to-br from-card to-background shadow-[0_0_20px_rgba(255,215,0,0.2),inset_0_0_15px_rgba(0,0,0,0.5)]">
                        <UploadIcon className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <p className="text-sm font-bold text-foreground tracking-wide">
                      {sportCategory === 'baseball'
                        ? (analysisMode === 'pitch-tipping' ? 'Drop pitching footage' : 'Drop baseball footage to analyze')
                        : sportCategory === 'golf'
                          ? 'Drop swing footage to analyze'
                          : sportCategory === 'soccer'
                            ? 'Drop match footage to analyze'
                            : 'Drop game footage to analyze'}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      MP4, MOV, PNG
                    </p>
                    <button
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="mt-3 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-sm font-bold rounded-full hover:shadow-[0_0_20px_rgba(255,215,0,0.5)] transition-all transform hover:scale-105"
                    >
                      Browse Files
                    </button>
                  </div>
                )}

                {/* YouTube URL Input */}
                {inputSource === 'youtube' && (
                  <div className="w-full max-w-xl flex flex-col items-center text-center border-2 border-dashed border-red-500/30 rounded-xl bg-red-500/5 p-6" onClick={(e) => e.stopPropagation()}>
                    <div className="relative w-16 h-16 mb-3">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-500/25 to-transparent animate-pulse" />
                      <div className="absolute inset-1.5 rounded-full bg-gradient-to-br from-card to-background border border-red-500/40 flex items-center justify-center shadow-[0_0_20px_rgba(239,68,68,0.2),inset_0_0_15px_rgba(0,0,0,0.5)]">
                        <svg className="w-6 h-6 text-red-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-foreground tracking-wide mb-3">
                      Search or Paste a YouTube URL
                    </p>

                    {/* Athlete Search */}
                    <div className="w-full space-y-3 mb-3">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={athleteSearch}
                            onChange={(e) => setAthleteSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAthleteSearch()}
                            placeholder={sportCategory === 'baseball'
                              ? "Search athlete name (e.g. Roansy Contreras)"
                              : sportCategory === 'golf'
                                ? "Search athlete name (e.g. Scottie Scheffler)"
                                : sportCategory === 'soccer'
                                  ? "Search athlete name (e.g. Kylian Mbappe)"
                                  : "Search athlete name (e.g. Victor Wembanyama)"}
                            className="w-full px-4 py-2.5 bg-background border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                          />
                          {isSearching && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <SlamLoader size={16} />
                            </div>
                          )}
                        </div>
                        <button
                          onClick={handleAthleteSearch}
                          disabled={isSearching || !athleteSearch.trim()}
                          className="px-4 py-2.5 bg-primary/20 hover:bg-primary/30 border border-primary/40 text-primary rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {isSearching ? 'Searching...' : 'Search'}
                        </button>
                      </div>

                      {/* Search Error */}
                      {searchError && (
                        <p className="text-xs text-red-400 px-1">{searchError}</p>
                      )}

                      {/* Search Results Grid — Multi-Select */}
                      {searchResults.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between px-1">
                            <span className="text-[10px] text-muted-foreground">
                              {selectedVideoIds.size > 0 ? `${selectedVideoIds.size} selected` : 'Click to select videos'}
                            </span>
                            <div className="flex gap-2">
                              {selectedVideoIds.size > 0 && (
                                <button
                                  onClick={clearSelectedVideos}
                                  className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  Clear All
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                            {searchResults.map((result) => {
                              const isSelected = selectedVideoIds.has(result.videoId);
                              return (
                                <button
                                  key={result.videoId}
                                  onClick={() => toggleVideoSelection(result)}
                                  className={`group text-left bg-card border rounded-lg overflow-hidden hover:shadow-lg transition-all ${
                                    isSelected
                                      ? 'border-primary ring-2 ring-primary/30 shadow-primary/10'
                                      : 'border-border/30 hover:border-primary/50 hover:shadow-primary/10'
                                  }`}
                                >
                                  <div className="relative aspect-video bg-muted">
                                    {result.thumbnail && (
                                      <img
                                        src={result.thumbnail}
                                        alt={result.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                      />
                                    )}
                                    {/* Selection checkbox overlay */}
                                    <div className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-md flex items-center justify-center transition-all ${
                                      isSelected
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-black/50 text-white/60 group-hover:bg-black/70 group-hover:text-white'
                                    }`}>
                                      {isSelected ? (
                                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                          <path d="M20 6L9 17l-5-5" />
                                        </svg>
                                      ) : (
                                        <svg className="w-3 h-3 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M12 5v14M5 12h14" />
                                        </svg>
                                      )}
                                    </div>
                                    {/* Video number badge when selected */}
                                    {isSelected && (
                                      <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                                        {[...selectedVideoIds].indexOf(result.videoId) + 1}
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-2">
                                    <p className="text-xs font-medium text-foreground line-clamp-2 leading-tight">{result.title}</p>
                                    <p className="text-[10px] text-muted-foreground mt-1 truncate">{result.channelTitle}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Selected Videos Tray */}
                      {selectedYoutubeVideos.length > 0 && (
                        <div className="w-full p-2 bg-primary/5 border border-primary/20 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-bold text-primary uppercase tracking-wider">
                              {selectedYoutubeVideos.length} Video{selectedYoutubeVideos.length !== 1 ? 's' : ''} Selected
                            </span>
                            {selectedYoutubeVideos.length > 8 && (
                              <span className="text-[10px] text-orange-400 font-medium">
                                Many videos may increase processing time
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2 overflow-x-auto pb-1">
                            {selectedYoutubeVideos.map((vid, idx) => (
                              <div key={vid.videoId} className="relative flex-shrink-0 w-20 group/thumb">
                                <div className="relative aspect-video rounded overflow-hidden bg-muted">
                                  {vid.thumbnail && (
                                    <img src={vid.thumbnail} alt={vid.title} className="w-full h-full object-cover" />
                                  )}
                                  <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                                    {idx + 1}
                                  </div>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); removeSelectedVideo(vid.videoId); }}
                                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                                  >
                                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                      <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                <p className="text-[9px] text-muted-foreground mt-0.5 line-clamp-1">{vid.title}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Divider with "or paste URL" */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-border/30" />
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">or paste URL directly</span>
                        <div className="flex-1 h-px bg-border/30" />
                      </div>
                    </div>

                    <div className="w-full flex gap-2">
                      <input
                        type="url"
                        value={youtubeUrl}
                        onChange={(e) => {
                          setYoutubeUrl(e.target.value);
                          if (error) setError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            pitcherMode ? handlePitcherAnalyze() : handleYoutubeAnalyze();
                          }
                        }}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="flex-1 px-4 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/30 transition-colors"
                      />
                      {!pitcherMode && (
                        <button
                          onClick={handleYoutubeAnalyze}
                          disabled={(!youtubeUrl.trim() && selectedYoutubeVideos.length === 0) || isYoutubeAnalyzing}
                          className="px-5 py-2.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold rounded-lg transition-all transform hover:scale-105 flex items-center gap-2"
                        >
                          {isYoutubeAnalyzing ? (
                            <SlamLoader size={16} color="white" />
                          ) : (
                            <SendIcon className="w-4 h-4" />
                          )}
                          {selectedYoutubeVideos.length > 1 ? `Analyze ${selectedYoutubeVideos.length} Videos` : 'Analyze'}
                        </button>
                      )}
                    </div>

                    {/* Deep Analysis Mode Toggle — baseball only */}
                    {sportCategory === 'baseball' && (
                    <div className="w-full mt-4 flex items-center justify-center gap-2">
                      <span className="text-[10px] text-muted-foreground">Mode:</span>
                      <button
                        onClick={() => { setPitcherMode(false); setPitcherResult(null); }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                          !pitcherMode
                            ? 'bg-red-500/20 border-red-500/50 text-red-400'
                            : 'bg-muted/20 border-border/40 text-muted-foreground hover:border-border'
                        }`}
                      >
                        General
                      </button>
                      <button
                        onClick={() => { setPitcherMode(true); setPitcherResult(null); }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                          pitcherMode
                            ? 'bg-primary/20 border-primary/60 text-primary'
                            : 'bg-muted/20 border-border/40 text-muted-foreground hover:border-border'
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 6v6l4 2" />
                          </svg>
                          Pitcher Eval
                        </span>
                      </button>
                    </div>
                    )}

                    {/* Pitcher Analysis Panel — baseball only */}
                    {sportCategory === 'baseball' && pitcherMode && (
                      <div className="w-full mt-4 space-y-3 text-left border-t border-border/30 pt-4">
                        {/* Player Name + Age */}
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1 block">Player Name</label>
                            <input
                              type="text"
                              value={playerName}
                              onChange={(e) => setPlayerName(e.target.value)}
                              placeholder={sportCategory === 'baseball' ? "e.g. Roansy Contreras" : sportCategory === 'golf' ? "e.g. Scottie Scheffler" : sportCategory === 'soccer' ? "e.g. Kylian Mbappe" : "e.g. Victor Wembanyama"}
                              maxLength={100}
                              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                            />
                          </div>
                          <div className="w-20">
                            <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1 block">Age</label>
                            <input
                              type="number"
                              value={playerAge}
                              onChange={(e) => setPlayerAge(Math.max(13, Math.min(55, parseInt(e.target.value) || 25)))}
                              min={13}
                              max={55}
                              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground text-center focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                            />
                          </div>
                        </div>

                        {/* Dimension Checkboxes */}
                        <div>
                          <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 block">Analysis Dimensions</label>
                          <div className="flex flex-wrap gap-1.5">
                            {[
                              { id: 'pitch_tipping', label: 'Tipping', color: 'red' },
                              { id: 'mechanical_efficiency', label: 'Mechanics', color: 'blue' },
                              { id: 'injury_risk', label: 'Injury Risk', color: 'orange' },
                              { id: 'pitch_optimization', label: 'Pitch Optimization', color: 'green' },
                              { id: 'development', label: 'Development', color: 'purple' },
                            ].map(dim => (
                              <button
                                key={dim.id}
                                onClick={() => toggleDimension(dim.id)}
                                className={`px-2.5 py-1 text-[11px] font-medium rounded-md border transition-all ${
                                  selectedDimensions.includes(dim.id)
                                    ? `bg-${dim.color}-500/20 border-${dim.color}-500/50 text-${dim.color}-400`
                                    : 'bg-muted/20 border-border/40 text-muted-foreground/60 hover:border-border'
                                }`}
                              >
                                <span className="flex items-center gap-1">
                                  {selectedDimensions.includes(dim.id) ? (
                                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>
                                  ) : (
                                    <svg className="w-3 h-3 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="3" /></svg>
                                  )}
                                  {dim.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Custom Prompt */}
                        <div>
                          <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1 block">Custom Instructions (Optional)</label>
                          <textarea
                            value={customPrompt}
                            onChange={(e) => setCustomPrompt(e.target.value)}
                            placeholder="e.g. Focus on arm slot consistency between fastball and slider..."
                            maxLength={2000}
                            rows={2}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 resize-none"
                          />
                        </div>

                        {/* Pitcher Analyze Button */}
                        <button
                          onClick={handlePitcherAnalyze}
                          disabled={(!youtubeUrl.trim() && selectedYoutubeVideos.length === 0) || !playerName.trim() || isYoutubeAnalyzing || selectedDimensions.length === 0}
                          className="w-full px-5 py-2.5 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg"
                        >
                          {isYoutubeAnalyzing ? (
                            <>
                              <SlamLoader size={16} color="white" />
                              Analyzing{selectedYoutubeVideos.length > 1 ? ` ${selectedYoutubeVideos.length} videos,` : ''} {selectedDimensions.length} Dimension{selectedDimensions.length !== 1 ? 's' : ''}...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 6v6l4 2" />
                              </svg>
                              Analyze{selectedYoutubeVideos.length > 1 ? ` ${selectedYoutubeVideos.length} Videos` : ' Pitcher'} ({selectedDimensions.length} Dim{selectedDimensions.length !== 1 ? 's' : ''})
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {!pitcherMode && (
                      <p className="text-[10px] text-muted-foreground mt-3">
                        Video will be downloaded, uploaded to GCS, and analyzed with Gemini
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {hasVideos && (
                <div className="flex-shrink-0 px-4 pt-4 pb-2 border-b border-border/30">
                  {/* Sync Status Indicator */}
                  {syncStatus && (
                    <div className="mb-3 flex items-center justify-center">
                      <div className="px-4 py-2 bg-primary/20 border border-primary/40 rounded-full flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                        <span className="text-xs font-medium text-primary">{syncStatus.message}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => stepFrame('back')} className="p-2 hover:text-primary transition-colors rounded-lg hover:bg-muted/50" title="Previous Frame">
                        <StepBackIcon className="w-5 h-5" />
                      </button>
                      <button onClick={togglePlay} className="p-2.5 bg-primary text-primary-foreground rounded-full hover:bg-primary/80 transition-transform active:scale-95 mx-1 shadow-lg">
                        {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5 pl-0.5" />}
                      </button>
                      <button onClick={() => stepFrame('forward')} className="p-2 hover:text-primary transition-colors rounded-lg hover:bg-muted/50" title="Next Frame">
                        <StepForwardIcon className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex-1 relative">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="0.1"
                        value={sliderValue}
                        onChange={handleSliderChange}
                        className="w-full h-6 focus:outline-none z-10 relative bg-transparent cursor-pointer accent-primary"
                      />
                    </div>

                    <div className="flex-shrink-0 px-3 py-1 bg-muted/50 rounded-full text-xs font-mono text-muted-foreground">
                      {videoCount} video{videoCount !== 1 ? 's' : ''}
                    </div>

                    {/* Reset Sync button - visible when start points are set */}
                    {videoStartPoints.size > 0 && (
                      <button
                        onClick={clearAllStartPoints}
                        className="flex-shrink-0 px-3 py-1 bg-primary/20 hover:bg-primary/30 text-primary rounded-full text-xs font-medium transition-colors flex items-center gap-1.5"
                        title="Clear all sync points"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                          <path d="M21 3v5h-5" />
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                          <path d="M3 21v-5h5" />
                        </svg>
                        Reset Sync
                      </button>
                    )}

                    {/* Drag hint */}
                    {items.length > 1 && (
                      <div className="flex-shrink-0 px-3 py-1 bg-primary/10 border border-primary/30 rounded-full text-xs font-medium text-primary/80 flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="9" cy="5" r="1" fill="currentColor" />
                          <circle cx="9" cy="12" r="1" fill="currentColor" />
                          <circle cx="9" cy="19" r="1" fill="currentColor" />
                          <circle cx="15" cy="5" r="1" fill="currentColor" />
                          <circle cx="15" cy="12" r="1" fill="currentColor" />
                          <circle cx="15" cy="19" r="1" fill="currentColor" />
                        </svg>
                        Drag to reorder
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-shrink-0 pl-3 border-l border-border/50">
                      {/* Grid Toggle */}
                      <button
                        onClick={() => setShowGrid(!showGrid)}
                        className={`p-1.5 rounded transition-colors ${showGrid ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-primary'}`}
                        title={showGrid ? 'Hide grid overlay' : 'Show grid overlay'}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <line x1="9" y1="3" x2="9" y2="21" />
                          <line x1="15" y1="3" x2="15" y2="21" />
                          <line x1="3" y1="9" x2="21" y2="9" />
                          <line x1="3" y1="15" x2="21" y2="15" />
                        </svg>
                      </button>
                      <div className="w-px h-4 bg-border/50" />
                      {/* Size Slider */}
                      <GridIcon className="w-4 h-4 text-muted-foreground" />
                      <input
                        type="range"
                        min="50"
                        max="150"
                        step="10"
                        value={thumbnailScale}
                        onChange={(e) => setThumbnailScale(parseInt(e.target.value))}
                        className="w-20 h-4 focus:outline-none bg-transparent cursor-pointer accent-primary"
                        title="Thumbnail size"
                      />
                      <span className="text-xs font-mono text-muted-foreground w-8">{thumbnailScale}%</span>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="flex-shrink-0 px-3 py-1.5 bg-muted/50 hover:bg-primary/20 text-muted-foreground hover:text-primary rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                    >
                      <UploadIcon className="w-3.5 h-3.5" />
                      Add
                    </button>
                  </div>
                </div>
              )}

              <div
                className="flex-1 p-4 overflow-auto slam-scrollbar"
                style={{
                  display: 'grid',
                  ...getGridStyle(),
                  alignContent: 'start',
                }}
              >
                {items.map((item, index) => (
                  <div
                    key={item.id}
                    className={`aspect-square transition-all duration-300 ease-out relative ${
                      draggedItemId === item.id
                        ? 'opacity-40 scale-90 rotate-2 z-50'
                        : dragOverItemId === item.id
                          ? 'ring-4 ring-primary/60 scale-[1.08] shadow-[0_0_25px_rgba(255,215,0,0.4)]'
                          : 'hover:scale-[1.02]'
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOverItem(e, item.id)}
                    onDrop={(e) => handleDropOnItem(e, item.id)}
                  >
                    {/* Drop indicator */}
                    {dragOverItemId === item.id && draggedItemId !== item.id && (
                      <div className="absolute -inset-1 rounded-xl border-4 border-primary/40 border-dashed pointer-events-none animate-pulse" />
                    )}
                    <MediaCard
                      item={item}
                      onRemove={handleRemove}
                      onVideoRef={handleVideoRef}
                      showGrid={showGrid}
                      startPoint={videoStartPoints.get(item.id) || 0}
                      onStartPointChange={handleStartPointChange}
                    />
                  </div>
                ))}
                {!hasVideos && (
                  <button
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                    className="aspect-square border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors bg-card/30 group"
                  >
                    <UploadIcon className="w-8 h-8 mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-medium">Add Media</span>
                  </button>
                )}
              </div>

              {/* Input Bar - Hidden during analysis */}
              {!isAnalyzing && (
                <div className="flex-shrink-0 px-4 py-3 border-t border-border/30 bg-card/30 space-y-3">
                  {/* Analysis Mode Selector (filtered by sport) */}
                  <AnalysisModeSelector
                    currentMode={analysisMode}
                    onModeChange={setAnalysisMode}
                    disabled={isAnalyzing}
                    sportCategory={sportCategory}
                  />

                  {/* Prompt Input Row */}
                  <div className="relative flex items-center gap-2 bg-background border border-border p-2 rounded-2xl shadow-inner focus-within:border-primary/50 transition-colors">
                    {/* Model Selector - uses stable callback to prevent flickering */}
                    <ModelSelector
                      key={`model-selector-${selectedModel}`}
                      selectedModel={selectedModel}
                      onModelChange={handleModelChange}
                      disabled={isAnalyzing}
                    />

                    <div className="w-px h-6 bg-border/50 flex-shrink-0" />

                    <input
                      type="text"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder={ANALYSIS_MODES[analysisMode]?.placeholder || 'Enter analysis prompt...'}
                      className="flex-1 bg-transparent border-none outline-none text-foreground placeholder-muted-foreground px-4 py-2 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAnalyze();
                        }
                      }}
                    />
                    {/* Quick Analysis Button — baseball only (simple-mechanics is baseball-specific) */}
                    {sportCategory === 'baseball' && (
                    <button
                      onClick={handleSimpleAnalysis}
                      disabled={items.length === 0}
                      className="px-3 py-2 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 disabled:opacity-50 disabled:cursor-not-allowed text-cyan-400 hover:text-cyan-300 transition-all active:scale-95 flex-shrink-0 flex items-center gap-1.5 text-xs font-medium"
                      title="Quick mechanical analysis with Flash model"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                      </svg>
                      Quick
                    </button>
                    )}
                    {/* Full Analysis Button */}
                    <button
                      onClick={handleAnalyze}
                      disabled={items.length === 0 && !prompt.trim()}
                      className="p-2.5 rounded-xl bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground transition-all shadow-lg active:scale-95 flex-shrink-0"
                      title="Full analysis with selected mode and model"
                    >
                      <SendIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Analysis mode hint */}
                  <div className="mt-1.5 flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground/60">
                    <span>{ANALYSIS_MODES[analysisMode]?.name || 'Analysis'}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        )}

        {/* Pitcher Analysis Results - uses unified StructuredAnalysisReport */}
        {pitcherResult && (
          <div ref={resultsPanelRef}>
            <StructuredAnalysisReport
              structuredData={{
                player_name: pitcherResult.player_name,
                overall_grade: pitcherResult.analysis?.overall_grade,
                executive_summary: pitcherResult.analysis?.executive_summary,
                top_3_action_items: pitcherResult.analysis?.top_3_action_items,
                dimensions: pitcherResult.analysis?.dimensions,
              }}
              analysisMode={analysisMode}
              model={pitcherResult.model}
              analysisMetadata={analysisMetadata}
              parseError={pitcherResult.parse_error}
              onClose={() => setPitcherResult(null)}
            />
          </div>
        )}

        {/* Results Area (standard markdown) */}
        <div ref={!pitcherResult ? resultsPanelRef : undefined}>
          {result && !isAnalyzing && (
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs text-muted-foreground">Analysis saved — navigating away won't erase this report.</span>
              <button
                onClick={() => { setResult(null); setAnalysisMetadata(null); }}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
              >
                <XIcon className="w-3 h-3" /> New Analysis
              </button>
            </div>
          )}
          <AnalysisPanel
            isLoading={isAnalyzing}
            result={result}
            mediaItems={items}
            analysisMode={analysisMode}
            analysisMetadata={analysisMetadata}
            onDrillDown={handleDrillDown}
            isDrillDownLoading={isDrillDownLoading}
          />
        </div>

        {/* Post-Analysis Chat - only visible after analysis completes */}
        {(result || pitcherResult) && !isAnalyzing && (
          <AnalysisChat
            mediaItems={items}
            analysisResult={result}
            analysisMode={analysisMode}
            analysisMetadata={analysisMetadata}
            pitcherResult={pitcherResult}
            contextFiles={contextFiles}
          />
        )}
      </main>

      {/* Analysis Complete Notification — compact toast */}
      {showNotification && (
        <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-right-5 fade-in duration-300">
          <button
            onClick={scrollToResults}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary/90 text-primary-foreground rounded-lg shadow-lg hover:bg-primary transition-all text-sm font-medium group"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
            Analysis Complete
            <svg className="w-3.5 h-3.5 group-hover:translate-y-0.5 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default VideoAnalyzer;
